import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragEndEvent,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import { setStatus } from "../api/supportApi";
import {
  PriorityBadge,
  STATUS_META,
  categoryLabel,
  priorityLabel,
  relativeTime,
  statusLabel,
} from "./meta";
import { TICKET_STATUSES, type TicketStatus, type TicketSummary } from "../types";

type T = Record<string, string>;

export function SupportBoard({
  tickets,
  t,
  language,
}: {
  tickets: TicketSummary[];
  t: T;
  language: string;
}) {
  const queryClient = useQueryClient();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const mutation = useMutation({
    mutationFn: ({ name, status }: { name: string; status: TicketStatus }) =>
      setStatus(name, status),
    // Optimistic move so the card lands instantly.
    onMutate: async ({ name, status }) => {
      await queryClient.cancelQueries({ queryKey: ["support", "tickets"] });
      const prev = queryClient.getQueryData<TicketSummary[]>(["support", "tickets"]);
      queryClient.setQueryData<TicketSummary[]>(["support", "tickets"], (old) =>
        old?.map((tk) => (tk.name === name ? { ...tk, status } : tk)),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["support", "tickets"], ctx.prev);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["support", "tickets"] }),
  });

  const byStatus = useMemo(
    () =>
      TICKET_STATUSES.reduce<Record<TicketStatus, TicketSummary[]>>((acc, s) => {
        acc[s] = tickets.filter((tk) => tk.status === s);
        return acc;
      }, {} as Record<TicketStatus, TicketSummary[]>),
    [tickets],
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const overId = e.over?.id as TicketStatus | undefined;
    const name = e.active.id as string;
    const from = e.active.data.current?.status as TicketStatus | undefined;
    if (overId && from && overId !== from) {
      mutation.mutate({ name, status: overId });
    }
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {TICKET_STATUSES.map((status) => (
          <Column
            key={status}
            status={status}
            label={statusLabel(t, status)}
            tickets={byStatus[status]}
            t={t}
            language={language}
          />
        ))}
      </div>
    </DndContext>
  );
}

function Column({
  status,
  label,
  tickets,
  t,
  language,
}: {
  status: TicketStatus;
  label: string;
  tickets: TicketSummary[];
  t: T;
  language: string;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  const meta = STATUS_META[status];
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex w-[264px] shrink-0 flex-col rounded-2xl border bg-gray-50/60 p-2 transition-colors dark:bg-gray-900/30",
        isOver
          ? "border-blue-300 bg-blue-50/60 dark:border-blue-500/40 dark:bg-blue-500/[0.06]"
          : "border-gray-200/70 dark:border-gray-800",
      )}
    >
      <div className="flex items-center gap-2 px-2 py-1.5">
        <span className={cn("h-2 w-2 rounded-full", meta.dot)} />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{label}</span>
        <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">{tickets.length}</span>
      </div>
      <div className="flex min-h-[80px] flex-col gap-2 p-1">
        {tickets.map((tk) => (
          <BoardCard key={tk.name} ticket={tk} t={t} language={language} />
        ))}
      </div>
    </div>
  );
}

function BoardCard({
  ticket,
  t,
  language,
}: {
  ticket: TicketSummary;
  t: T;
  language: string;
}) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: ticket.name,
    data: { status: ticket.status },
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform) }}
      {...listeners}
      {...attributes}
      onClick={() => navigate(`/support/${ticket.name}`)}
      className={cn(
        "cursor-grab touch-none rounded-xl border border-gray-200 bg-white p-3 text-left shadow-sm transition-shadow hover:shadow active:cursor-grabbing dark:border-gray-800 dark:bg-gray-900/60",
        isDragging && "opacity-60 shadow-lg",
      )}
    >
      <div className="flex items-start gap-1.5">
        {ticket.unread && (
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
        )}
        <p className="line-clamp-2 text-sm font-medium text-gray-900 dark:text-gray-100">
          {ticket.subject}
        </p>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-400 dark:text-gray-500">
        <PriorityBadge priority={ticket.priority} label={priorityLabel(t, ticket.priority)} />
        <span>·</span>
        <span>{categoryLabel(t, ticket.category)}</span>
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[11px] text-gray-400 dark:text-gray-500">
        <span className="font-mono">{ticket.name}</span>
        <span className="inline-flex items-center gap-1">
          <MessageSquare className="h-3 w-3" />
          {ticket.messageCount}
          <span className="mx-1">·</span>
          {relativeTime(ticket.lastActivityOn, language)}
        </span>
      </div>
    </div>
  );
}
